/**
 * Official CAI — curated, per-artifact membership.
 *
 * Membership is an explicit, PR-gated allowlist (`../data/official-cai.json`), never
 * inferred from who committed the code, which org owns the repo, or a repo-wide blanket.
 * `isOfficial` is a read-time membership check; it never persists anything on the entry.
 *
 * See docs/v2/official-cai-curation.md for the intake → approval → list-PR workflow.
 */

import type { MarketplaceEntry } from "../types/index";
import allowlistData from "../data/official-cai.json";

export interface OfficialEntry {
  /**
   * Plugin identity = the DynamoDB partition key:
   * `${source}#${orgOrOwner}/${repoName}`, with `#${skillPath}` appended for a
   * multi-manifest artifact. Must match the catalog entry's fields exactly (same casing).
   */
  id: string;
  /** Audit metadata: where the approved plugin.json lives (e.g. ".claude-plugin/plugin.json"). */
  pluginJsonLocation?: string;
  approvedBy: string;
  approvedAt: string;
}

export interface OfficialAllowlist {
  updatedAt?: string;
  docs?: string;
  entries: OfficialEntry[];
}

const allowlist: OfficialAllowlist = allowlistData;

/**
 * Canonical plugin identity key — mirrors the DynamoDB PK construction in
 * processor-lambda (field-mapper / plugin-store-client). A `skillPath` distinguishes
 * independent multi-manifest artifacts that share a repo; synthesized sub-skills carry
 * no `skillPath` and therefore inherit their parent root plugin's identity.
 */
export function officialIdentityKey(
  p: Pick<MarketplaceEntry, "source" | "orgOrOwner" | "repoName" | "skillPath">,
): string {
  const base = `${p.source}#${p.orgOrOwner}/${p.repoName}`;
  return p.skillPath ? `${base}#${p.skillPath}` : base;
}

// Membership is matched case-insensitively. GitHub org and repo names are
// case-insensitive (no two repos can differ only by case), so a curator copying
// an `id` from a repo URL never has to reproduce its exact casing.
const normalizeKey = (key: string): string => key.toLowerCase();

// Keyed by normalized identity for O(1) membership and metadata lookup. A later
// duplicate id wins, mirroring last-write semantics; the allowlist is PR-gated so
// duplicates shouldn't occur in practice.
const officialEntries = new Map(allowlist.entries.map((e) => [normalizeKey(e.id), e]));

/** True when the entry's plugin identity is on the curated Official CAI allowlist. */
export function isOfficial(p: MarketplaceEntry | null | undefined): boolean {
  if (!p) return false;
  return officialEntries.has(normalizeKey(officialIdentityKey(p)));
}

/** The matching allowlist entry (approval metadata) for an Official artifact, else undefined. */
export function getOfficialEntry(p: MarketplaceEntry | null | undefined): OfficialEntry | undefined {
  if (!p) return undefined;
  return officialEntries.get(normalizeKey(officialIdentityKey(p)));
}
